import React, { useState } from "react"
import { Quote, Star } from "lucide-react"
import { TestimonialItem } from "../types/objects"
import { SectionHeading } from "./ui/SectionHeading"

/**
 * Props interface for Testimonials component
 */
interface TestimonialsProps {
    data?: TestimonialItem[]
}

/** Initials used when the avatar image is missing */
const initials = (name: string) =>
    name
        .split(/[\s,]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join("")

/**
 * Avatar with graceful fallback to initials when the image cannot be loaded.
 */
const Avatar: React.FC<{ src?: string; name: string }> = ({ src, name }) => {
    const [failed, setFailed] = useState(false)

    if (!src || failed) {
        return <span className="lp-avatar" aria-hidden="true">{initials(name)}</span>
    }

    return <img className="lp-avatar" src={src} alt={name} onError={() => setFailed(true)} loading="lazy" />
}

/**
 * Testimonials Component — client quotes in equal-height cards.
 */
export const Testimonials: React.FC<TestimonialsProps> = ({ data }) => {
    const renderStars = (rating: number) => {
        const value = Math.max(0, Math.min(5, Math.round(rating)))
        return (
            <div className="lp-quote__stars" aria-label={`Hodnocení ${rating} z 5`}>
                {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={16} fill={i < value ? "currentColor" : "none"} strokeWidth={i < value ? 0 : 1.5} />
                ))}
            </div>
        )
    }

    return (
        <section id="testimonials" className="lp-section lp-section--soft">
            <div className="lp-container">
                <SectionHeading
                    eyebrow="Reference"
                    title="Reference"
                    lead="V této sekci uvádíme krátké reference našich klientů – jejich zkušenosti s naší spoluprací a dosaženými výsledky."
                />

                <div className="lp-grid lp-grid--3">
                    {data ? (
                        data.map((item, idx) => (
                            <figure
                                key={`${item.name}-${idx}`}
                                className="lp-card lp-quote"
                                data-reveal=""
                                style={{ margin: 0, "--reveal-delay": `${idx * 100}ms` } as React.CSSProperties}
                            >
                                <Quote className="lp-quote__mark" size={44} aria-hidden="true" />
                                {typeof item.rating === "number" && renderStars(item.rating)}
                                <blockquote className="lp-quote__text" style={{ margin: 0, padding: 0, border: 0 }}>
                                    &bdquo;{item.text}&ldquo;
                                </blockquote>
                                <figcaption className="lp-quote__author">
                                    <Avatar src={item.img} name={item.name} />
                                    <span className="lp-quote__name">{item.name}</span>
                                </figcaption>
                            </figure>
                        ))
                    ) : (
                        <p>Načítání referencí...</p>
                    )}
                </div>
            </div>
        </section>
    )
}
